import { Edge } from "@xyflow/react";

import { DecisionsOutcomeFilter } from "src/api/decisionHistoryV2/decisionHistoryQueries";
import { DatasetColumn, DatasetPurpose } from "src/api/types";
import {
  Chart,
  EdgeDb,
  FlowDb,
  FlowDbShallowVersionNoPolicy,
  FlowMeta,
  FlowVersionDb,
  FlowVersionDbShallowChildren,
  FlowVersionMeta as FlowVersionMetaBase,
  GraphDb,
  NodeDb,
  NodeGroupDb,
} from "src/clients/flow-api";
import { OrganizationInDB } from "src/clients/taktile-api";
import { BeMappedNode, GroupNode } from "src/constants/NodeDataTypes";
import { JsonRefPath } from "src/schema/jsonRefUtils";
import { ValueOf } from "src/utils/typeUtils";

// This type is used in multiple places and hard to avoid.
// Allow `any` to use here to avoid refactoring all the code.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type GenericObject = { [key: string]: any };

export type NodeBE = Omit<NodeDb, "meta"> & {
  meta: GenericObject;
};

export type Graph = Omit<GraphDb, "nodes" | "edges" | "node_groups"> & {
  nodes: NodeBE[];
  edges: EdgeDb[];
  node_groups: NodeGroupDb[];
};

// All the supported types from JSON Schema
export const SchemaTypesBE = {
  string: "string",
  boolean: "boolean",
  number: "number",
  integer: "integer",
  object: "object",
  array: "array",
} as const;

// readonly so the schema building functions don't mess with the default here
export const anyJsonSchemaType = Object.values(
  SchemaTypesBE,
) as readonly ValueOf<typeof SchemaTypesBE>[];

export const InputSchemaTypes = {
  ...SchemaTypesBE,
  "datetime-str": "datetime-str",
  "date-str": "date-str",
  datetime: "datetime",
  date: "date",
  any: "any",
  enum: "enum",
  "file[]": "file[]",
} as const;

export const OutputSchemaTypes = {
  ...SchemaTypesBE,
  datetime: "datetime",
  date: "date",
  any: "any",
  enum: "enum",
} as const;

export const SchemaTypes = {
  ...InputSchemaTypes,
  ...OutputSchemaTypes,
  union: "union",
} as const;

type NullString = "null";

export type SchemaTypes = keyof typeof SchemaTypes | JsonRefPath;
export const isSchemaType = (type: string): type is SchemaTypes => {
  return Object.keys(SchemaTypes).includes(type);
};

export type SchemaTypesBE = keyof typeof SchemaTypesBE;

export type PropertyTypeUI = [
  type: SchemaTypes | JsonRefPath,
  nullable: NullString | false,
];
export type PropertyTypeBE =
  // undefined and single SchemaTypesBET for backward compatibility
  undefined | SchemaTypesBE | (SchemaTypesBE | NullString)[];
export type PropertyTitleBE =
  | "native-date"
  | "native-datetime"
  | "CaseTemplateInput"
  | undefined;
export type EnumOptionsBE = (string | number | boolean | null)[];

export type JsonSchemaItems = {
  $ref?: string;
  enum?: EnumOptionsBE;
  format?: string;
  title?: string;
  type?: SchemaTypesBE | SchemaTypesBE[];
};

// Non-standard fields that are not part of the official JSON schema spec
// The sensitive field is currently only used for input schemas in the backend.
// With the upcoming rework it could makes sense to define separate Input and Ouput schema types.
export type SchemaExtensions = {
  order?: string[];
  sensitive?: string[];
};

export type PropertyValue = {
  type?: PropertyTypeBE;
  title?: PropertyTitleBE;
  properties?: Record<string, GenericObject>;
  description?: string;
  required?: string[];
  format?: string;
  pattern?: string;
  // In $comment we used to store the unix timestamp when the property was created
  $comment?: string;
  items?: JsonSchemaItems;
  enum?: EnumOptionsBE;
  tags?: EnumOptionsBE;
  $ref?: string;
  anyOf?: {
    type?: SchemaTypesBE | "null";
    $ref?: JsonRefPath;
    enum?: EnumOptionsBE;
    items?: JsonSchemaItems;
  }[];
  oneOf?: {
    type?: SchemaTypesBE | "null";
    $ref?: JsonRefPath;
    enum?: EnumOptionsBE;
    items?: JsonSchemaItems;
  }[];
} & SchemaExtensions;

export type Schema<T = PropertyValue> = {
  $schema: string;
  type: "object";
  $defs?: Record<string, SubSchema<T>>;
  definitions?: Record<string, SubSchema<T>>; // same as "$defs", draft-07 and older
  properties: Record<string, T>;
  required?: string[];
} & SchemaExtensions;

export type SubSchema<T = PropertyValue> = {
  type: PropertyTypeBE;
  default?: unknown;
  properties?: Record<string, T>;
  required?: string[];
} & SchemaExtensions;

export type FlowVersionMeta = FlowVersionMetaBase & {
  number_decisions?: number;
};

export type FlowVersion = Omit<
  FlowVersionDb,
  | "meta"
  | "input_schema"
  | "output_schema"
  | "graph"
  | "child_versions"
  | "parent_versions"
> & {
  name: string;
  graph?: Graph;
  meta: Omit<
    FlowVersionMeta,
    // deprecated fields
    | "created_by_avatar"
    | "created_by"
    | "updated_by"
    | "updated_by_avater"
    | "published_by"
    | "published_by_avatar"
    | "archived_by"
    | "archived_by_avatar"
  >;
  published_name?: string;
  /**
   * Not present on shallow versions
   */
  input_schema?: Schema;
  /**
   * Not present on shallow versions
   */
  output_schema?: Schema;
  /**
   * Only available on versions queried directly from a flow version endpoint
   */
  child_versions?: FlowVersionDb["child_versions"];
  /**
   * Only available on versions queried directly from a flow version endpoint
   */
  parent_versions?: FlowVersionDb["parent_versions"];
};

/**
 * Flow-api returns slightly different types depending on which endpoint returns them.
 * These extra fields are returned when the version is listed as part of a flow but not
 * when the version is queried on its own. We should consider switching to using backend types for flow-api.
 */
export type FlowVersionFlowChild = FlowVersion & {
  child_flows?: FlowVersionDbShallowChildren["child_flows"];
  parent_flows?: FlowVersionDbShallowChildren["parent_flows"];
  review?: FlowVersionDbShallowChildren["review"] | null;
};

export type Flow = Omit<
  FlowDb,
  "versions" | "charts" | "chart_order" | "meta" | "flow_folder_id"
> & {
  // Typed wrongly by the generated api (given as string? when the right typing is string | null)
  flow_folder_id: string | null;
  versions: FlowVersionFlowChild[];
  decisionsLastMonth?: string;
  parent_flows?: FlowDbShallowVersionNoPolicy[];
  child_flows?: FlowDbShallowVersionNoPolicy[];
  meta: FlowMeta;
  charts: Chart[];
  chart_order: string[];
  ui_default_decision_fields_exclude: string[];
  ui_default_inputs_include: string[];
  ui_default_outputs_include: string[];
  ui_default_outcomes_include: string[];
};

export interface DecisionFilters {
  flow_versions: string[];
  status_codes: string[];
  start_date: string;
  end_date: string;
  traffic_policy_id?: string;
  job_run_id?: string;
  outcome_filters?: DecisionsOutcomeFilter[];
}

export type CreateDatasetFromHistory = {
  name: string;
  flow_id: string;
  flow_slug: string;
  filters: DecisionFilters;
  input_columns_to_overwrite: DatasetColumn[];
  outcome_columns: DatasetColumn[];
  sub_sampling_size?: number;
  mock_node_names_to_include?: string[];
  output_column_names_to_include?: string[];
  estimated_total_row_count?: number;
};

export type CreateSimulationDatasetJob = {
  name: string;
  flow_id: string;
  filters: DecisionFilters;
  input_columns_to_overwrite: DatasetColumn[];
  //sub_sampling_size?: number;
  //mock_node_names_to_include?: string[];
  //output_column_names_to_include?: string[];
};

export type CreateDatasetFromScratch = {
  name: string;
  flow_id: string;
  input_columns: DatasetColumn[];
  mock_columns: DatasetColumn[];
  purpose: DatasetPurpose;
};

export type Organization = Pick<
  OrganizationInDB,
  | "allows_decide_access"
  | "whitelabel"
  | "allows_signup"
  | "is_active"
  | "user_provisioning_mode"
> & {
  id: string;
  name: string;
  principal: string;
  // The "not present" state is null, but the generated types don't represent that.
  partner?: OrganizationInDB["partner"] | null;
  is_active?: boolean;
};

export { FlowVersionStatus } from "src/clients/flow-api";

export type NodeUpsert = Record<string, Nullable<BeMappedNode>>;
export type EdgeUpsert = Record<string, Nullable<Edge>>;
export type GroupUpsert = Record<string, Nullable<GroupNode>>;

export type FlowGroup = {
  flow: Flow;
};

export type FlowGroupForCount = {
  flowId: string;
  count: number;
};
